/**
 * Data hook for the placeholder palette: fetches the placeholder list once, keeps only Active items, applies
 * the free-text filter, and buckets the result into {@link PaletteGroup}s. The caller owns the query string
 * (search box state); this hook owns fetching and derivation.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import { productsPlaceholderList } from '@/api/generated/products-placeholders/products-placeholders';
import type { PlaceholderListItem } from '@/api/generated/schemas';
import { PlaceholderStatus, PlaceholderType } from '@/api/generated/schemas';
import { buildGroups, haystack, type PaletteGroup } from './paletteGroups';

const PALETTE_PAGE_SIZE = 500;

interface UsePalettePlaceholdersResult {
  isLoading: boolean;
  /** Active items matching the current search (before grouping). */
  filtered: PlaceholderListItem[];
  groups: PaletteGroup[];
}

interface UsePalettePlaceholdersOptions {
  /** `false` u nulanočního programu — ubytování se pak z nabídky vypustí i s jeho skupinou. */
  allowAccommodation: boolean;
}

export function usePalettePlaceholders(
  query: string,
  t: TFunction,
  { allowAccommodation }: UsePalettePlaceholdersOptions
): UsePalettePlaceholdersResult {
  const listQuery = useQuery({
    queryKey: ['venua-planner', 'costScheme', 'palette'],
    queryFn: ({ signal }) =>
      productsPlaceholderList({ page: 1, pageSize: PALETTE_PAGE_SIZE }, signal),
  });

  const activeItems = useMemo(
    () =>
      (listQuery.data?.items ?? []).filter(
        (item) =>
          item.status === PlaceholderStatus.Active &&
          (allowAccommodation || item.type !== PlaceholderType.Accommodation)
      ),
    [listQuery.data, allowAccommodation]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activeItems;
    return activeItems.filter((item) => haystack(item).includes(q));
  }, [activeItems, query]);

  const groups = useMemo(() => {
    // Nulanoční program nemá kde ubytovat — skupina ubytování se nesmí objevit ani prázdná, jinak by
    // z ní šel přetáhnout prázdný box.
    const all = buildGroups(filtered, t).filter(
      (group) => allowAccommodation || group.type !== PlaceholderType.Accommodation
    );
    // Every type is always shown so its blank "drag to create" box stays available even before any
    // placeholder exists — except while searching, where empty groups would just be noise.
    return query.trim() === '' ? all : all.filter((group) => group.items.length > 0);
  }, [filtered, query, t, allowAccommodation]);

  return { isLoading: listQuery.isLoading, filtered, groups };
}
